import React from 'react';
import { useNode } from '@craftjs/core';
import { TextField, FormControl, FormLabel, Typography, Box, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import * as Icons from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: 'relative',
    cursor: 'pointer',
    border: '2px dashed transparent',
    transition: 'border-color 0.2s',
    width: '100%',
    '&:hover': {
      borderColor: '#2196f3',
    },
  },
  wrapperSelected: {
    borderColor: '#2196f3',
    borderStyle: 'solid',
  },
  badge: {
    position: 'absolute',
    top: 6,
    left: 6,
    zIndex: 10,
    pointerEvents: 'none',
    backgroundColor: '#2196f3',
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 700,
    height: 22,
    borderRadius: 3,
    padding: '0 8px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
  },
  // Overlay semi-transparente para bloquear interacciones con el HTML
  overlay: {
    position: 'absolute',
    inset: 0,
    zIndex: 5,
    cursor: 'pointer',
    backgroundColor: 'transparent',
  },
  htmlContent: {
    width: '100%',
    // Limitar altura máxima en el editor para no ocupar todo el canvas
    maxHeight: '80vh',
    overflow: 'auto',
  },
  settings: {
    padding: theme.spacing(2),
  },
  labelField: {
    marginBottom: theme.spacing(2),
  },
  htmlTextarea: {
    width: '100%',
    fontFamily: '"Roboto Mono", monospace',
    fontSize: '0.72rem',
    padding: theme.spacing(1),
    border: '1px solid #ccc',
    borderRadius: 4,
    minHeight: 220,
    resize: 'vertical',
    outline: 'none',
    lineHeight: 1.5,
    color: '#333',
    backgroundColor: '#fafafa',
    '&:focus': {
      borderColor: '#2196f3',
      backgroundColor: '#fff',
    },
  },
  hint: {
    marginTop: theme.spacing(0.5),
    fontSize: '0.72rem',
    color: '#999',
  },
  statsRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    flexWrap: 'wrap',
  },
  statChip: {
    height: 20,
    fontSize: '0.68rem',
  },
}));

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
export const HtmlBlock = ({ html, label }) => {
  const classes = useStyles();
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((node) => ({ selected: node.events.selected }));

  return (
    <div
      ref={(ref) => connect(drag(ref))}
      className={`${classes.wrapper} ${selected ? classes.wrapperSelected : ''}`}
    >
      {/* Overlay para bloquear clics en el HTML mientras se edita en el editor */}
      <div className={classes.overlay} />

      {/* Badge con nombre del bloque (visible al hover/selección) */}
      {selected && (
        <span className={classes.badge}>
          <Icons.Code style={{ fontSize: 12 }} />
          {label || 'Bloque HTML'}
        </span>
      )}

      {/* HTML renderizado */}
      <div
        className={classes.htmlContent}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Panel de configuración (Settings Panel)
// ---------------------------------------------------------------------------
const HtmlBlockSettings = () => {
  const classes = useStyles();
  const {
    actions: { setProp },
    html,
    label,
  } = useNode((node) => ({
    html:  node.data.props.html,
    label: node.data.props.label,
  }));

  // Estadísticas básicas del HTML
  const charCount  = html ? html.length : 0;
  const lineCount  = html ? html.split('\n').length : 0;
  const tagMatches = html ? html.match(/<[a-z][^>]*>/gi) : null;
  const tagCount   = tagMatches ? tagMatches.length : 0;

  return (
    <div className={classes.settings}>
      {/* Nombre del bloque */}
      <FormControl fullWidth className={classes.labelField}>
        <FormLabel>Nombre del bloque</FormLabel>
        <TextField
          value={label || ''}
          onChange={(e) => setProp((props) => (props.label = e.target.value))}
          variant="outlined"
          size="small"
          placeholder="Ej: Encabezado, Sección principal…"
          style={{ marginTop: 6 }}
        />
      </FormControl>

      {/* Estadísticas */}
      {html && (
        <Box className={classes.statsRow}>
          <Chip
            className={classes.statChip}
            size="small"
            icon={<Icons.TextFields style={{ fontSize: 12 }} />}
            label={`${charCount.toLocaleString()} caracteres`}
          />
          <Chip
            className={classes.statChip}
            size="small"
            icon={<Icons.FormatListNumbered style={{ fontSize: 12 }} />}
            label={`${lineCount} líneas`}
          />
          <Chip
            className={classes.statChip}
            size="small"
            icon={<Icons.Code style={{ fontSize: 12 }} />}
            label={`${tagCount} etiquetas`}
          />
        </Box>
      )}

      {/* Editor HTML */}
      <FormControl fullWidth>
        <FormLabel>Código HTML</FormLabel>
        <Typography className={classes.hint}>
          Edita el HTML directamente. Los cambios se reflejan en tiempo real en el canvas.
        </Typography>
        <textarea
          className={classes.htmlTextarea}
          value={html || ''}
          onChange={(e) => setProp((props) => (props.html = e.target.value))}
          spellCheck={false}
          autoComplete="off"
          style={{ marginTop: 8 }}
        />
      </FormControl>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Craft config
// ---------------------------------------------------------------------------
HtmlBlock.craft = {
  props: {
    html:  '',
    label: 'Bloque HTML',
  },
  related: {
    settings: HtmlBlockSettings,
  },
};
